'use client';

import { type ElementType } from 'react';
import { Eye, LayoutGrid, Settings2, ShieldCheck, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getRegrasTokens,
  type RegrasAppearanceTokens,
} from '@/lib/regras-ui-tokens';
import type { ConductAppearance } from '@/lib/conduct-presentation';

export type RegrasCategoryFilterItem = {
  name: string;
  count: number;
};

type CategoryMeta = {
  icon: ElementType;
};

const CATEGORY_ICONS: Record<string, CategoryMeta> = {
  Todas: { icon: LayoutGrid },
  Comportamento: { icon: Users },
  Conteúdo: { icon: Eye },
  Segurança: { icon: ShieldCheck },
  Sistema: { icon: Settings2 },
};

const FALLBACK: CategoryMeta = { icon: Users };

interface RegrasCategoryFilterProps {
  categories: RegrasCategoryFilterItem[];
  selected: string;
  onSelect: (name: string) => void;
  appearance?: ConductAppearance;
}

export default function RegrasCategoryFilter({
  categories,
  selected,
  onSelect,
  appearance = 'dark',
}: RegrasCategoryFilterProps) {
  const t: RegrasAppearanceTokens = getRegrasTokens(appearance);
  const isLight = appearance === 'light';

  return (
    <section className="relative z-20 -mt-8 px-6 sm:-mt-10">
      <div className="mx-auto flex max-w-7xl justify-start">
        <div
          className={cn(
            'relative w-fit max-w-full overflow-hidden rounded-2xl border px-2 py-2 backdrop-blur-md',
            t.filterTray
          )}
        >
          <div
            className={cn(
              'pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent',
              isLight ? 'via-[rgba(0,11,106,0.22)]' : 'via-sky-400/40'
            )}
            aria-hidden
          />
          {!isLight ? (
            <div
              className="pointer-events-none absolute -left-8 top-1/2 h-20 w-20 -translate-y-1/2 rounded-full bg-sky-400/15 blur-2xl"
              aria-hidden
            />
          ) : null}

          <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div
              className="flex min-w-min items-center gap-1 p-0.5"
              role="tablist"
              aria-label="Filtrar regras por categoria"
            >
              {categories.map((cat) => {
                const isActive = selected === cat.name;
                const Icon = (CATEGORY_ICONS[cat.name] ?? FALLBACK).icon;

                return (
                  <button
                    key={cat.name}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => onSelect(cat.name)}
                    className={cn(
                      'group/tab relative flex shrink-0 items-center gap-2.5 rounded-xl border px-3 py-2',
                      'font-poppins text-[13px] font-semibold tracking-tight outline-none',
                      'transition-[color,background-color,border-color,box-shadow] duration-200',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                      isLight
                        ? 'focus-visible:ring-[rgba(0,11,106,0.35)] focus-visible:ring-offset-white'
                        : 'focus-visible:ring-sky-400/40 focus-visible:ring-offset-[#030023]',
                      isActive ? t.filterActive : t.filterInactive
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors duration-200',
                        isActive ? t.filterIconActive : t.filterIconInactive
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    <span className="flex flex-col items-start gap-0.5">
                      <span className="whitespace-nowrap leading-none">{cat.name}</span>
                      <span
                        className={cn(
                          'text-[10px] font-medium leading-none tracking-wide',
                          isActive
                            ? isLight
                              ? 'text-white/65'
                              : 'text-sky-200/70'
                            : isLight
                              ? 'text-slate-400'
                              : 'text-sky-400/50'
                        )}
                      >
                        {cat.count === 1 ? '1 regra' : `${cat.count} regras`}
                      </span>
                    </span>
                    <span
                      className={cn(
                        'ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums leading-none',
                        isActive ? t.filterCountActive : t.filterCountInactive
                      )}
                    >
                      {cat.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const ruleCategoryConfig: Record<string, { dot: string; icon: ElementType }> = {
  Comportamento: { dot: 'bg-sky-400', icon: Users },
  Conteúdo: { dot: 'bg-sky-300', icon: Eye },
  Segurança: { dot: 'bg-blue-400', icon: ShieldCheck },
  Sistema: { dot: 'bg-indigo-400', icon: Settings2 },
};

export const ruleCategoryConfigLight: Record<string, { dot: string; icon: ElementType }> = {
  Comportamento: { dot: 'bg-[#000b6a]', icon: Users },
  Conteúdo: { dot: 'bg-[#000b6a]/80', icon: Eye },
  Segurança: { dot: 'bg-[#000b6a]/70', icon: ShieldCheck },
  Sistema: { dot: 'bg-[#000b6a]/60', icon: Settings2 },
};

export const ruleCategoryFallback = { dot: 'bg-sky-400', icon: Users };
export const ruleCategoryFallbackLight = { dot: 'bg-[#000b6a]', icon: Users };
